import { useEffect, useState } from "react";
import { toast } from "sonner";
import { MapPin } from "lucide-react";
import { api, errMsg, eur, serviceAmount, servicePriceLabel, todayStr, TIME_BANDS } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { LoginGate } from "./LoginGate";

export function HomeCareForm() {
  const { user } = useAuth();
  const [services, setServices] = useState([]);
  const [f, setF] = useState({ service_id: "", city: "", address: "", date: todayStr(), time_band: "mattina", notes: "" });
  const [pros, setPros] = useState(null);
  const [pro, setPro] = useState(null);
  const [sent, setSent] = useState(false);
  const service = services.find((s) => s.id === f.service_id);
  const set = (k) => (e) => setF({ ...f, [k]: e?.target ? e.target.value : e });

  useEffect(() => {
    api.get("/services", { params: { kind: "domicilio" } }).then(({ data }) => setServices(data));
  }, []);

  const search = async () => {
    if (!service || !f.city) return toast.error("Seleziona prestazione e località");
    const { data } = await api.get("/professionals", { params: { kind: "domicilio", specialty: service.specialty, city: f.city } });
    setPros(data);
    setPro(null);
  };

  const submit = async () => {
    try {
      await api.post("/appointments", { ...f, professional_id: pro.id });
      setSent(true);
      toast.success("Richiesta inviata con successo");
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  if (sent) {
    return (
      <div data-testid="homecare-sent" className="rounded-2xl border border-emerald-200 bg-emerald-50 p-5 text-sm text-emerald-800">
        Richiesta inviata. La segreteria o il professionista la confermeranno; riceverai una notifica e potrai pagare dalla tua Area personale.
      </div>
    );
  }
  return (
    <div className="space-y-4" data-testid="homecare-form">
      <div className="space-y-1.5">
        <Label>Prestazione</Label>
        <Select value={f.service_id} onValueChange={set("service_id")}>
          <SelectTrigger data-testid="homecare-service-select"><SelectValue placeholder="Scegli prestazione" /></SelectTrigger>
          <SelectContent>
            {services.map((s) => <SelectItem key={s.id} value={s.id} data-testid={`homecare-service-${s.id}`}>{s.name} · {servicePriceLabel(s)}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>
      <div className="grid gap-3 sm:grid-cols-2">
        <div className="space-y-1.5"><Label>Località</Label><Input data-testid="homecare-city-input" value={f.city} onChange={set("city")} placeholder="es. Roma" /></div>
        <div className="space-y-1.5"><Label>Indirizzo</Label><Input data-testid="homecare-address-input" value={f.address} onChange={set("address")} placeholder="Via, civico" /></div>
        <div className="space-y-1.5"><Label>Data</Label><Input type="date" min={todayStr()} data-testid="homecare-date-input" value={f.date} onChange={set("date")} /></div>
        <div className="space-y-1.5">
          <Label>Fascia oraria</Label>
          <Select value={f.time_band} onValueChange={set("time_band")}>
            <SelectTrigger data-testid="homecare-timeband-select"><SelectValue /></SelectTrigger>
            <SelectContent>{Object.entries(TIME_BANDS).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}</SelectContent>
          </Select>
        </div>
      </div>
      <Button data-testid="homecare-search-btn" variant="outline" onClick={search} className="w-full rounded-full"><MapPin className="mr-2 h-4 w-4" /> Mostra professionisti disponibili</Button>
      {pros && (
        <div className="grid gap-2" data-testid="homecare-professionals">
          {pros.length === 0 && <p className="text-sm text-slate-500">Nessun professionista disponibile in questa località. Prova Roma o Milano (dati demo).</p>}
          {pros.map((p) => (
            <button key={p.id} data-testid={`homecare-pro-${p.id}`} onClick={() => setPro(p)} className={`rounded-xl border px-4 py-3 text-left text-sm transition-colors ${pro?.id === p.id ? "border-sky-600 bg-sky-50" : "border-slate-200 bg-white hover:border-cyan-400"}`}>
              <b>{p.title} {p.name}</b><span className="block text-xs text-slate-500">{p.specialty} · {p.cities.join(", ")}</span>
            </button>
          ))}
        </div>
      )}
      {pro && (user ? (
        <>
          <Textarea data-testid="homecare-notes-input" value={f.notes} onChange={set("notes")} placeholder="Note logistiche (citofono, piano…)" />
          <Button data-testid="homecare-submit-btn" onClick={submit} className="w-full rounded-full bg-sky-600 hover:bg-sky-700">Invia richiesta · {eur(serviceAmount(service))}</Button>
        </>
      ) : <LoginGate />)}
    </div>
  );
}
